import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Send } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

/** Monthly nudge to whoever owns a stale article — a list nobody is emailed is
 *  a list nobody reads. */
export default function KbReviewDigestCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/kb/review-digest").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSaving(true);
    try {
      const { data } = await api.put("/kb/review-digest", {
        enabled: next.enabled, overdue_days: Number(next.overdue_days) || 365,
        max_rows: Number(next.max_rows) || 25,
      });
      setCfg((c) => ({ ...c, ...data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/kb/review-digest/send-now");
      toast.success(data.sent
        ? `Sent to ${data.sent} owner${data.sent === 1 ? "" : "s"} covering ${data.articles} articles`
        : data.reason || "Nothing overdue right now");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the digest");
    } finally { setSending(false); }
  };

  if (!cfg) return null;
  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="kb-review-digest-card">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-4 w-4 text-slate-500" />
        <h2 className="font-heading text-base font-semibold text-slate-800">Stale article review digest</h2>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        On the 1st of each month every review owner gets one email listing the published articles
        they own that nobody has touched in a year.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ enabled: v })}
            disabled={!cfg.can_edit} data-testid="kb-review-digest-enabled" />
          Monthly digest on
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          Overdue after
          <Input type="number" min={30} max={1825} value={cfg.overdue_days}
            onChange={(e) => setCfg({ ...cfg, overdue_days: e.target.value })}
            onBlur={() => save({})} disabled={!cfg.can_edit}
            className="w-24" data-testid="kb-review-digest-days" />
          days
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          Max rows per email
          <Input type="number" min={5} max={100} value={cfg.max_rows}
            onChange={(e) => setCfg({ ...cfg, max_rows: e.target.value })}
            onBlur={() => save({})} disabled={!cfg.can_edit}
            className="w-20" data-testid="kb-review-digest-rows" />
        </label>
        <Button variant="outline" onClick={sendNow} disabled={!cfg.can_edit || sending}
          data-testid="kb-review-digest-send">
          {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
          Send now
        </Button>
        {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <p className="mt-2 text-xs text-slate-500" data-testid="kb-review-digest-summary">
        {(cfg.owners || []).map((o) => `${o.name}: ${o.count}`).join(" · ") || "Nobody owns any overdue articles"}
        {cfg.unowned ? ` · ${cfg.unowned} overdue with no owner` : ""}
        {cfg.last_sent_at ? ` · last sent ${String(cfg.last_sent_at).slice(0, 10)}` : ""}
      </p>
    </section>
  );
}
